(() => {
  'use strict';

  const doc = document;
  const body = doc.body;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = window.matchMedia('(pointer: fine)').matches;

  const header = doc.querySelector('[data-neo-header]');
  const progress = doc.querySelector('[data-page-progress]');
  let scrollTicking = false;

  const updateScrollState = () => {
    const y = window.scrollY || 0;
    if (header) header.classList.toggle('is-compact', y > 40);
    if (progress) {
      const max = Math.max(1, doc.documentElement.scrollHeight - window.innerHeight);
      progress.style.setProperty('--an-progress', `${Math.min(100, (y / max) * 100)}%`);
    }
    scrollTicking = false;
  };

  window.addEventListener('scroll', () => {
    if (!scrollTicking) {
      scrollTicking = true;
      requestAnimationFrame(updateScrollState);
    }
  }, { passive: true });
  updateScrollState();

  const mobileMenu = doc.querySelector('[data-mobile-menu]');
  const menuClose = doc.querySelector('[data-menu-close]');
  const syncMenu = () => body.classList.toggle('an-menu-open', Boolean(mobileMenu && mobileMenu.open));
  if (mobileMenu) mobileMenu.addEventListener('toggle', syncMenu);
  if (menuClose && mobileMenu) menuClose.addEventListener('click', () => { mobileMenu.removeAttribute('open'); syncMenu(); });
  if (mobileMenu) mobileMenu.querySelectorAll('a').forEach(link => link.addEventListener('click', () => mobileMenu.removeAttribute('open')));

  const megaMenus = [...doc.querySelectorAll('[data-mega-menu]')];
  megaMenus.forEach(menu => menu.addEventListener('toggle', () => {
    if (!menu.open) return;
    megaMenus.forEach(other => { if (other !== menu) other.removeAttribute('open'); });
  }));
  doc.addEventListener('click', event => {
    megaMenus.forEach(menu => { if (menu.open && !menu.contains(event.target)) menu.removeAttribute('open'); });
  });
  doc.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    megaMenus.forEach(menu => menu.removeAttribute('open'));
    if (mobileMenu) mobileMenu.removeAttribute('open');
  });

  const reveals = [...doc.querySelectorAll('[data-neo-reveal]')];
  if (!reduceMotion && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -5% 0px' });
    reveals.forEach(element => observer.observe(element));
  } else {
    reveals.forEach(element => element.classList.add('is-visible'));
  }

  const hero = doc.querySelector('[data-neo-hero]');
  if (hero) {
    const slides = [...hero.querySelectorAll('[data-hero-slide]')];
    const dots = [...hero.querySelectorAll('[data-hero-dot]')];
    const current = hero.querySelector('[data-hero-current]');
    let active = 0;
    let heroTimer;

    const showSlide = index => {
      active = (index + slides.length) % slides.length;
      slides.forEach((slide, i) => {
        const selected = i === active;
        slide.classList.toggle('is-active', selected);
        slide.setAttribute('aria-hidden', selected ? 'false' : 'true');
      });
      dots.forEach((dot, i) => dot.classList.toggle('is-active', i === active));
      if (current) current.textContent = String(active + 1).padStart(2, '0');
      if (!reduceMotion && dots[active]) {
        const bar = dots[active].querySelector('span');
        if (bar) { bar.style.animation = 'none'; void bar.offsetWidth; bar.style.animation = ''; }
      }
    };

    const stopHero = () => clearInterval(heroTimer);
    const startHero = () => {
      stopHero();
      if (!reduceMotion && slides.length > 1) heroTimer = setInterval(() => showSlide(active + 1), 6200);
    };

    dots.forEach((dot, i) => dot.addEventListener('click', () => { showSlide(i); startHero(); }));
    hero.addEventListener('pointerenter', stopHero);
    hero.addEventListener('pointerleave', startHero);
    hero.addEventListener('focusin', stopHero);
    hero.addEventListener('focusout', startHero);
    showSlide(0);
    startHero();
  }

  const rail = doc.querySelector('[data-product-rail]');
  if (rail) {
    const prev = doc.querySelector('[data-rail-prev]');
    const next = doc.querySelector('[data-rail-next]');
    let railTimer;
    const step = direction => {
      const card = rail.querySelector('.an-product-card');
      const width = (card ? card.getBoundingClientRect().width : 320) + 16;
      rail.scrollBy({ left: direction * width, behavior: reduceMotion ? 'auto' : 'smooth' });
    };
    if (prev) prev.addEventListener('click', () => step(-1));
    if (next) next.addEventListener('click', () => step(1));
    const stopRail = () => clearInterval(railTimer);
    const startRail = () => {
      stopRail();
      if (!reduceMotion && window.innerWidth > 900) railTimer = setInterval(() => {
        const atEnd = rail.scrollLeft >= rail.scrollWidth - rail.clientWidth - 20;
        if (atEnd) rail.scrollTo({ left: 0, behavior: 'smooth' }); else step(1);
      }, 4800);
    };
    rail.addEventListener('pointerenter', stopRail);
    rail.addEventListener('pointerleave', startRail);
    rail.addEventListener('touchstart', stopRail, { passive: true });
    startRail();
  }

  const story = doc.querySelector('[data-story-root]');
  if (story) {
    const buttons = [...story.querySelectorAll('[data-story-button]')];
    const images = [...story.querySelectorAll('[data-story-image]')];
    const current = story.querySelector('[data-story-current]');
    let storyIndex = 0;
    let storyTimer;
    const selectStory = index => {
      storyIndex = (index + buttons.length) % buttons.length;
      buttons.forEach((button, i) => button.classList.toggle('is-active', i === storyIndex));
      images.forEach((image, i) => image.classList.toggle('is-active', i === storyIndex));
      if (current) current.textContent = String(storyIndex + 1).padStart(2, '0');
    };
    const stopStory = () => clearInterval(storyTimer);
    const startStory = () => {
      stopStory();
      if (!reduceMotion && buttons.length > 1) storyTimer = setInterval(() => selectStory(storyIndex + 1), 5200);
    };
    buttons.forEach((button, i) => button.addEventListener('click', () => { selectStory(i); startStory(); }));
    story.addEventListener('pointerenter', stopStory);
    story.addEventListener('pointerleave', startStory);
    startStory();
  }

  doc.querySelectorAll('[data-copy-code]').forEach(button => {
    button.addEventListener('click', async () => {
      const label = button.querySelector('b');
      const original = label ? label.textContent : '';
      try { if (navigator.clipboard) await navigator.clipboard.writeText('ALOURA10'); } catch (error) { /* code remains visible */ }
      if (label) {
        label.textContent = 'Code copié ✓';
        window.setTimeout(() => { label.textContent = original; }, 1800);
      }
    });
  });

  if (finePointer && !reduceMotion) {
    const glow = doc.querySelector('[data-cursor-glow]');
    if (glow) {
      let pointerTicking = false;
      let pointerX = 0;
      let pointerY = 0;
      window.addEventListener('pointermove', event => {
        pointerX = event.clientX;
        pointerY = event.clientY;
        if (pointerTicking) return;
        pointerTicking = true;
        requestAnimationFrame(() => {
          glow.style.transform = `translate3d(${pointerX - 110}px,${pointerY - 110}px,0)`;
          body.classList.add('is-pointer');
          pointerTicking = false;
        });
      }, { passive: true });
      doc.documentElement.addEventListener('mouseleave', () => body.classList.remove('is-pointer'));
    }
  }
})();
